// nexus-publico-util — esc + formatação de data para as páginas PÚBLICAS
// (rsvp, vitrine, ingresso).
//
// R30 #1 e #3 da AUDITORIA-R29-R30: `esc`/`escHtml` e `dataBR`/`quando` (SQL UTC →
// local pt-BR) reinventados em app/assets/js/eticket.js, app/rsvp/index.html e
// app/vitrine/index.html — cada um com o mesmo comentário sobre o bug do fuso
// ("sem o 'Z' o V8 lê como hora local").
//
// Decisão de arquitetura (28/09/2026): as 3 páginas continuam SEM ES modules —
// isto aqui é SCRIPT CLÁSSICO (sem import/export), incluído por
// `<script src="/assets/js/nexus/publico-util.js">` ANTES do script da página.
// Funciona nos dois regimes que as 3 páginas usam hoje: `<script>` comum (rsvp) e
// `<script type="module">` (vitrine, ingresso) — global de script clássico
// continua visível dentro de um módulo carregado depois no mesmo documento.
//
// `esc()` é byte a byte o corpo de `esc.js` (mesma lista de 5 caracteres, mesmo
// motivo do apóstrofo — "sem ele, atributo entre aspas simples escapa do próprio
// delimitador"). Duas FONTES da mesma lógica, não duas implementações:
// `tests/publico-util.test.mjs` prova que os dois corpos batem e falha por
// mutação se divergirem — não dava para `esc.js` reexportar aqui (é módulo ES) nem
// este arquivo importar de lá (script clássico não tem `import`).
//
// `paraData` não estava no recorte original (só esc/dataBR/dataHoraBR), mas o
// ingresso precisa do Date cru para montar o .ics/link do Google Agenda, e a
// vitrine precisa dele para a contagem "Faltam N dias" — sem expor isto aqui, o
// parse do UTC nasceria de novo dentro de uma das duas, uma 4ª cópia da mesma
// lógica que este arquivo existe para fechar.
//
// `diasAte` entrou em 05/10/2026 (arena de higiene): a previsão acima se
// cumpriu do jeito errado — a vitrine foi buscar a mesma conta em
// `diasAteCalendario()`, cópia local sob outro nome. Corpo idêntico ao de
// `nexus1/js/tempo.js:diasAte` (import não dá: lá é módulo ES, aqui é script
// clássico); `tests/publico-util.test.mjs` prova a igualdade, no molde de `esc`.
(function () {
	const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

	function esc(v) {
		return String(v ?? '').replace(/[&<>"']/g, c => ESCAPES[c]);
	}

	// O servidor manda datetime SQL UTC sem fuso ('2026-11-14 22:00:00'); sem o
	// 'Z' o V8 lê como hora LOCAL do visitante e a data pula ~3h — o mesmo bug que
	// deixava o DTSTAMP do .ics nulo no ingresso (RFC 5545 recusa o arquivo sem
	// ele). Quem já vem com 'Z' ou offset explícito (±HH:mm) — caso do
	// `new Date().toISOString()` no próprio ingresso — não ganha um 'Z' duplicado.
	function paraData(s) {
		if (!s) return null;
		const t = String(s).replace(' ', 'T');
		const d = new Date(/[Z]|[+-]\d{2}:?\d{2}$/.test(t) ? t : t + 'Z');
		return isNaN(d) ? null : d;
	}

	// Formato único (ingresso + rsvp hoje): "quarta-feira, 14 de novembro de 2026
	// 22:00". `opts` troca o formato inteiro — o ingresso usa isso para "usado em
	// 14/11 22:00" (ingresso já lido).
	function dataBR(s, opts) {
		const d = paraData(s);
		if (!d) return '';
		return d.toLocaleString('pt-BR', opts ?? {
			weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
			hour: '2-digit', minute: '2-digit',
		});
	}

	// Formato "dia · hora" (vitrine hoje): data por extenso e hora separadas por
	// ' · ' — visualmente diferente de `dataBR` (sem vírgula entre data e hora, dia
	// numérico e não 2-digit). Duas strings de saída distintas porque as duas
	// páginas já mostravam formatos distintos — consolidar a LÓGICA de parse não
	// significa forçar um único formato de EXIBIÇÃO.
	function dataHoraBR(s) {
		const d = paraData(s);
		if (!d) return '';
		return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
			+ ' · ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
	}

	// Dias inteiros entre hoje e o evento, por DIA DE CALENDÁRIO — não por
	// múltiplo de 24h: "faltam 15 dias" é afirmação sobre o calendário, e um
	// evento às 19h a 14 dias e 6h não é "14". `null` = sem data (diferente de
	// zero, não compara com número). Mesmo corpo de `tempo.js:diasAte`.
	function diasAte(quando, hoje = new Date()) {
		if (!quando) return null;
		const s = String(quando).replace(' ', 'T');
		const d = new Date(/[Z]|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + 'Z');
		if (isNaN(d)) return null;
		const meiaNoite = (x) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
		return Math.round((meiaNoite(d) - meiaNoite(hoje)) / 86400000);
	}

	window.NexusPublico = { esc, paraData, dataBR, dataHoraBR, diasAte };
})();
